import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { HighlightService } from 'app/core/services/highlight.service';
import { HighlightType } from 'app/shared/enums/highlight-type.enum';
import { HtmlHelper } from 'app/shared/helpers/html-helper';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-grouped-label-card',
  styleUrls: ['./grouped-label-card.component.scss'],
  templateUrl: './grouped-label-card.component.html',
  imports: [NgClass, MatIcon, MatTooltip, MatIconButton],
})
export class GroupedLabelCardComponent {
  @Input() public attachmentActiveTooltip = 'Consulter les fichiers joints';
  @Input() public attachmentInactiveTooltip = 'Aucun fichier joint';
  @Input() public commentActiveTooltip = 'Consulter les notes internes';
  @Input() public commentInactiveTooltip = 'Aucune note interne';
  @Input() public disabled: boolean | null = false;
  @Output() public firstIconClickedEvent = new EventEmitter<void>();
  @Input() public group: string | null | undefined;
  @Input() public groupCanceledFlag: boolean | undefined = false;
  @Input() public groupDisabledFlag: boolean | null | undefined = false;
  @Input() public groupNote: string | undefined;
  @Input() public groupPrefixIcon: string | null | undefined;
  @Input() public groupPrefixIconTooltip: string | null | undefined;
  @Input() public groupProceedFlag: boolean | undefined = false;
  @Input() public groupStyle: 'active-text' | 'accent-fg' | 'customer-area-fg' | 'primary-fg' | undefined = 'primary-fg';
  @Input() public hasAttachment = false;
  @Input() public hasComment = false;
  @Input() public hat: string | null | undefined;
  @Input() public hatDisabledFlag: boolean | undefined = false;
  @Input() public hatStyle: 'accent' | 'accent-border' | 'patch' | 'primary' | 'primary-border' | undefined = 'accent';
  @Input() public hatWidth: 'auto' | 'fixed' = 'auto';
  @Input() public highlightGroupFlag = false;
  @Input() public highlightGroupType = HighlightType.String;
  @Input() public highlightHatFlag = false;
  @Input() public highlightInfoFlag = false;
  @Input() public highlightLabelFlag = false;
  @Input() public highlightLabelType = HighlightType.String;
  @Input() public highlightSubgroupFlag = false;
  @Input() public highlightTitleFlag = false;
  @Input() public info: string | null | undefined;
  @Input() public infoHighlightType = HighlightType.String;
  @Input() public infoIcon: string;
  @Input() public infoLine2: string | null;
  @Input() public infoLine2HighlightType = HighlightType.String;
  @Input() public infoLine2Icon: string;
  @Input() public infoLine2Style: 'accent-fg' | 'customer-area-fg' | 'primary-fg' | '' = '';
  @Input() public infoLine2WarnFlag = false;
  @Input() public infoLine3: string | null;
  @Input() public infoLine3HighlightType = HighlightType.String;
  @Input() public infoUnspecifiedFlag = false;
  @Input() public label: string | null | undefined;
  @Input() public labelCanceledFlag = false;
  @Input() public labelDefaultFlag = false;
  @Input() public labelDisabledFlag: boolean | null | undefined = false;
  @Input() public labelProceedFlag = false;
  @Input() public labelStyle: 'active-text' | 'default' | 'medium-text' | 'small-text' | undefined = 'default';
  @Input() public labelUnspecifiedFlag = false;
  @Input() public productionFlowProcessingActionFlag: boolean | undefined = false;
  @Output() public secondIconClickedEvent = new EventEmitter<void>();
  @Input() public showAttachmentIcon = false;
  @Input() public showCommentIcon = false;
  @Input() public subGroup: string | null | undefined;
  @Input() public subGroupDisabledFlag: boolean | null | undefined = false;
  @Input() public tags: string[] | null | undefined;
  @Input() public title: string;
  @Input() public truncatedContentWidth: string;

  private _highlightService = inject(HighlightService);

  public highlightGroup(content: string | null | undefined): string | null | undefined {
    return this.highlightInternal(content, this.highlightGroupFlag, this.highlightGroupType);
  }

  public highlightHat(content: string): string | null | undefined {
    return this.highlightInternal(content, this.highlightHatFlag);
  }

  public highlightInfo(content: string): string | null | undefined {
    return this.highlightInternal(content, this.highlightInfoFlag, this.infoHighlightType);
  }

  public highlightInfoLine2(content: string): string | null | undefined {
    return this.highlightInternal(content, this.highlightInfoFlag, this.infoLine2HighlightType);
  }

  public highlightInfoLine3(content: string): string | null | undefined {
    return this.highlightInternal(content, this.highlightInfoFlag, this.infoLine3HighlightType);
  }

  public highlightLabel(content: string): string | null | undefined {
    return this.highlightInternal(content, this.highlightLabelFlag, this.highlightLabelType);
  }

  public highlightSubgroup(content: string): string | null | undefined {
    return this.highlightInternal(content, this.highlightSubgroupFlag);
  }

  public highlightTag(content: string): string | null | undefined {
    return this.highlightInternal(content, true);
  }

  public highlightTitle(content: string): string | null | undefined {
    return this.highlightInternal(content, this.highlightTitleFlag);
  }

  private highlightInternal(
    content: string | null | undefined,
    highlightFlag: boolean,
    highlightType = HighlightType.String,
  ): string | null | undefined {
    if (highlightFlag) {
      return this._highlightService.highlight(content, highlightType);
    }

    return HtmlHelper.escape(content);
  }
}
